/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useRef, useCallback, useMemo } from "react"
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom"
import { Box, useTheme } from "@mui/material"

import {
  IndoorMap,
  getSuitablyValueBetweenBearings,
  getLocationIdByFeature,
} from "components/IndoorMap"

import { useVenue } from "providers/venue"
import { useRoute } from "providers/route"
import { useGeoLocation } from "providers/geolocation"
import _ from "lodash"
import config from "../config"
import { useAppUI } from "../providers/ui"
import { featureCollection } from "@turf/helpers"
import { Q_VENUE } from "constant"

const FALLBACK_CENTER = [100.5017051, 13.7572619]
const DEFAULT_ZOOM = 18.5
const DEFAULT_MIN_ZOOM = null
const DEFAULT_MAX_ZOOM = 22

export const IndoorMapContainer = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const {
    isLoading,
    userLocation,
    scannedLocations,
    deviceLocation,
    locationVenue,
  } = useGeoLocation()
  const {
    venues,
    features,
    amenities,
    labels,
    decorations,
    findFeatureById,
    dataLoaded,
    transformLocationToFeature,
    transformStepsToGeometries,
    relationshipGraphLoaded,
    mapTheme,
    mapConfig,
  } = useVenue()

  const { mapReady, setmapReady } = useAppUI()
  const { route } = useRoute()
  const indoorRef = useRef(null)
  const [mapLoaded, setMapLoaded] = useState(false)
  const [mapLayout, setMapLayout] = useState("full")
  const theme = useTheme()
  const styleOverrides = theme.components[`AppMap`]?.styleOverrides || {}
  const { indoorMapContainer } = styleOverrides

  const {
    origin: originParams,
    destination: destinationParams,
    step,
    ordinal,
  } = useParams()
  const [searchParams] = useSearchParams()

  const defaultCenter = useMemo(
    () =>
      _.get(venues, "[0].properties.display_point.coordinates") ||
      FALLBACK_CENTER,
    [venues]
  )

  const defaultBearing = useMemo(
    () => _.get(mapConfig, "default_bearing", 0),
    [mapConfig]
  )
  const defaultPitch = useMemo(
    () => _.get(mapConfig, "default_pitch", 45),
    [mapConfig]
  )

  const zoomOptions = useMemo(() => {
    const minZoom = _.get(mapConfig, "min_zoom", DEFAULT_MIN_ZOOM)
    const maxZoom = _.get(mapConfig, "max_zoom", DEFAULT_MAX_ZOOM)
    return minZoom < maxZoom
      ? { minZoom, maxZoom }
      : { minZoom: DEFAULT_MIN_ZOOM, maxZoom: DEFAULT_MAX_ZOOM }
  }, [mapConfig])

  const minZoom = useMemo(() => _.get(zoomOptions, "minZoom"), [zoomOptions])

  const maxZoom = useMemo(() => _.get(zoomOptions, "maxZoom"), [zoomOptions])

  const targetOrigin = useMemo(
    () => transformLocationToFeature(originParams),
    [originParams, transformLocationToFeature]
  )

  const targetDestination = useMemo(
    () => transformLocationToFeature(destinationParams),
    [destinationParams, transformLocationToFeature]
  )
  const isKioskMode = useMemo(
    () => ["kiosk-v", "kiosk-h"].includes(config("device")),
    []
  )

  const pixelRatio = 2

  const getAmenityIdsFromUnitIds = useCallback(
    (unitIds) => {
      const matchingAmenities = amenities.filter((amenity) =>
        amenity.properties?.unit_ids.some((id) => unitIds.includes(id))
      )
      return matchingAmenities.map((amenity) => amenity.id)
    },
    [amenities]
  )

  useEffect(() => {
    /* Handle User location
     * @Eample: /maps/ordinal/2?user-location=98.29877730421258,7.892412617738349,2o
     */
    if (!indoorRef.current) return

    if (mapLoaded && isKioskMode && deviceLocation) {
      indoorRef.current?.addUserLocation(deviceLocation)
    } else if (mapLoaded && userLocation) {
      indoorRef.current?.addUserLocation(userLocation)
    } else {
      indoorRef.current?.removeUserLocation()
    }
  }, [userLocation, mapLoaded, deviceLocation, indoorRef.current])

  useEffect(() => {
    /**
     * Handle last scanned locations
     */
    if (!indoorRef.current) return

    //TODO:: make this logic supports multiple locations?
    const scannedLocation = _.last(scannedLocations)
    if (mapReady && scannedLocation) {
      indoorRef.current?.removeLastUserLocation()
      indoorRef.current?.addLastUserLocation(scannedLocation)
    } else {
      indoorRef.current?.removeLastUserLocation()
    }
  }, [scannedLocations, mapReady, deviceLocation, indoorRef.current])

  useEffect(() => {
    if (
      !indoorRef.current ||
      !mapLoaded ||
      !dataLoaded ||
      !mapTheme ||
      !relationshipGraphLoaded
    )
      return

    if (location.pathname !== "/" && location.pathname.indexOf("/maps") !== 0) {
      // Hide Map
      setMapLayout("hidden")
      return
    }

    const [action, value] = location.pathname.replace("/maps/", "").split("/")

    setMapLayout("full")

    // Handle the map's direction and the map's interactive
    switch (action) {
      case "dir":
        indoorRef.current.disableClick(true)
        indoorRef.current.hideUserLocationMarker()
        indoorRef.current.hideLastUserLocationMarker()
        break
      case "":
        indoorRef.current.clearNavigationGeometries()
        indoorRef.current.showUserLocationMarker()
        indoorRef.current.disableClick()
        break
      default:
        indoorRef.current.clearNavigationGeometries()
        indoorRef.current.showUserLocationMarker()
        indoorRef.current.enableClick()
        indoorRef.current.setFeatureObject3DsOpacity(1)
        break
    }

    // Handle the map's rendering
    switch (action) {
      case "place": {
        indoorRef.current.showVenueObjects()
        const feature = findFeatureById(value)
        const element_id = getLocationIdByFeature(feature)

        if (element_id) {
          indoorRef.current.changeLevelByOrdinal(
            Number(feature?.properties?.ordinal || 0)
          )
          indoorRef.current.clearHighlightElements()
          indoorRef.current.clearHighlightObject()
          const highlightedFeature = indoorRef.current.setHighlightElementIds([
            element_id,
          ])
          // Handle Fly to animation
          const extent = indoorRef.current.getFeatureExtent(highlightedFeature)
          const center = indoorRef.current.getExtentCenter(extent)
          const zoom = indoorRef.current.getExtentZoom(extent)
          indoorRef.current.setHighlightedObject([element_id])
          indoorRef.current.flyTo(center, { zoom, pitch: 40 })
        }
        break
      }

      case "ordinal": {
        indoorRef.current.showVenueObjects()
        const currentOrdinal = Number(ordinal)
        const viewingVenueId = searchParams.get(Q_VENUE)
        const venue = findFeatureById(viewingVenueId) || locationVenue

        const venueDisplayCoordinates = _.get(
          venue,
          "properties.display_point.coordinates"
        )
        const venueDefaultZoom = _.get(venue, "properties.zoom")

        const userLocationReference = isKioskMode
          ? deviceLocation
          : userLocation
        const userOrdinal = _.get(userLocationReference, "properties.ordinal")
        const userCoords = _.get(userLocationReference, "geometry.coordinates")
        const shouldFocusUser =
          !!userLocationReference && userOrdinal !== null && userCoords !== null

        indoorRef.current.changeLevelByOrdinal(currentOrdinal)
        if (venue && venue.feature_type === "venue") {
          const currentBearing = indoorRef.current.getBearing()
          const bearing = getSuitablyValueBetweenBearings(
            defaultBearing,
            currentBearing
          )
          const pitch = defaultPitch
          const extent = indoorRef.current.getFeatureExtent(venue)
          const center = shouldFocusUser
            ? userCoords
            : venueDisplayCoordinates ||
              indoorRef.current.getExtentCenter(extent)
          const zoom =
            venueDefaultZoom ||
            indoorRef.current.getExtentZoom(extent) +
              (shouldFocusUser ? 1.5 : 1) //adjust offsets

          indoorRef.current.flyTo(center, {
            zoom,
            bearing,
            pitch,
          })
        }
        indoorRef.current.clearHighlightElements()
        indoorRef.current.clearHighlightObject()
        // navigate(`/maps/${ordinal !== null ? `ordinal/${ordinal}${search}` : ""}`)
        break
      }
      case "dir": {
        indoorRef.current.hideVenueObjects()
        if (!targetOrigin && targetDestination && userLocation) {
          navigate(
            `/maps/dir/${userLocation.properties.userLocationParam}/${targetDestination?.id}`,
            { replace: true }
          )
          return
        }

        if (
          isKioskMode &&
          !targetOrigin &&
          targetDestination &&
          deviceLocation
        ) {
          navigate(
            `/maps/dir/${deviceLocation.properties.userLocationParam}/${targetDestination?.id}`,
            { replace: true }
          )
          return
        }

        if (!targetOrigin || !targetDestination) {
          // Clear path in the route path Layer when the origin or destination input was empty
          indoorRef.current.clearNavigationGeometries()

          if (!targetOrigin && !targetDestination) {
            indoorRef.current.clearHighlightElements()
            indoorRef.current.clearHighlightObject()
            return
          }

          // Add highlight to the origin or detination element (if exist)
          const targetFeature = targetOrigin || targetDestination
          const element_id = getLocationIdByFeature(
            targetOrigin || targetDestination
          )
          if (element_id) {
            indoorRef.current.changeLevelByOrdinal(
              Number(targetFeature?.properties?.ordinal || 0)
            )
            indoorRef.current.clearHighlightElements()
            indoorRef.current.clearHighlightObject()
            const highlightedFeature = indoorRef.current.setHighlightElementIds(
              [element_id]
            )
            // Handle Fly to animation
            const extent =
              indoorRef.current.getFeatureExtent(highlightedFeature)
            const center = indoorRef.current.getExtentCenter(extent)
            const zoom = indoorRef.current.getExtentZoom(extent)
            indoorRef.current.flyTo(center, { zoom })
            indoorRef.current.setHighlightedObject([element_id])
          }
          return
        }

        indoorRef.current.clearHighlightElements()
        indoorRef.current.clearHighlightObject()
        if (!route) return

        const navigationGeometries = transformStepsToGeometries(route.steps)
        const viewingOrdinal = Number(
          searchParams.get("ordinal") || route.origin.properties.ordinal
        )

        //Clear path in the route path Layer
        indoorRef.current.clearNavigationGeometries()

        // Create route path on map
        indoorRef.current.createNavigationGeometries(navigationGeometries)

        //Change level on map by ordinal
        indoorRef.current.changeLevelByOrdinal(viewingOrdinal)

        //create current overview step path by ordinal
        const path = indoorRef.current.createOverviewStepPathByOrdinal(
          navigationGeometries,
          viewingOrdinal
        )

        //animate fly to and display overview step path by current ordinal
        indoorRef.current.flyToOverviewStepPath(path)

        if (step) {
          // handle the step page
          const stepNumber = parseInt(step)
          const targetStep = navigationGeometries.find(
            ({ id }) => id === `step-${stepNumber}`
          )

          if (!targetStep) return

          indoorRef.current.clearHighlightObject()

          const { id, properties, isVerticalStep, isLastStep, geometry } =
            targetStep
          const { ordinal, intermediaryUnits } = properties

          //only show geometry, path and maker in current ordinal
          indoorRef.current.changeLevelByOrdinal(ordinal)

          if (isVerticalStep) {
            //verticalStep's intermediaries are elevators/escalators/stairs
            const amenitiesToHighlight =
              getAmenityIdsFromUnitIds(intermediaryUnits)
            //highlight elevators/escalators/stairs markers
            indoorRef.current.setHighlightedObject(amenitiesToHighlight)
          }

          //If this is vertical step or last step will animate fly to and zooming in on the geometry.
          if (isVerticalStep || isLastStep) {
            indoorRef.current.flyToAndZoomIn(geometry.coordinates)
            return
          }

          //animate fly to and display the path
          const targetGeometryInLayer =
            indoorRef.current.getTargetGeometryInLayer(ordinal, id)
          if (!targetGeometryInLayer) return
          indoorRef.current.flyToPath(targetGeometryInLayer)
          //set opacity of extrudedUnits to 0.5
          indoorRef.current.setFeatureObject3DsOpacity(0.5)
        }

        // hide origin and destination logo / text marker
        indoorRef.current.hideGeometryByElementId(route.origin.id)
        indoorRef.current.hideGeometryByElementId(route.destination.id)
        break
      }
      case "amenities": {
        indoorRef.current.hideVenueObjects()
        const viewingVenueId = searchParams.get(Q_VENUE)
        const viewingVenue = findFeatureById(viewingVenueId) || locationVenue

        const viewingOrdinalQuery = searchParams.get("ordinal")
        const currentFloorOrdinal = Number(viewingOrdinalQuery)

        if (!_.isEmpty(viewingOrdinalQuery)) {
          const amenitiesByCategoryByVenue = amenities.filter(
            (amenity) =>
              amenity.properties.category === value &&
              amenity.properties.venue.id === viewingVenue?.id
          )

          const filteredAmenitiesOnOrdinal = amenitiesByCategoryByVenue.filter(
            (amenity) => amenity.properties.ordinal === currentFloorOrdinal
          )
          const filteredAmenitiesIds = filteredAmenitiesOnOrdinal.map(
            (amenity) => amenity.id
          )

          try {
            indoorRef.current.changeLevelByOrdinal(currentFloorOrdinal)
            indoorRef.current.clearHighlightElements()
            indoorRef.current.clearHighlightObject()

            const highlightedFeatures =
              indoorRef.current.setHighlightElementIds(filteredAmenitiesIds)
            indoorRef.current.setHighlightedObject(filteredAmenitiesIds)

            if (highlightedFeatures) {
              // Handle Fly to animation
              const zoomOffset = filteredAmenitiesIds.length > 1 ? -0.1 : -3
              const extent =
                indoorRef.current.getFeatureExtent(highlightedFeatures)
              const center = indoorRef.current.getExtentCenter(extent)
              const zoom = indoorRef.current.getExtentZoom(extent)
              indoorRef.current.flyTo(center, {
                zoom: zoom - zoomOffset,
              })
            } else {
              const currentBearing = indoorRef.current.getBearing()
              const bearing = getSuitablyValueBetweenBearings(
                defaultBearing,
                currentBearing
              )
              indoorRef.current.flyTo(defaultCenter, {
                zoom: DEFAULT_ZOOM,
                bearing,
                pitch: 45,
              })
            }
          } catch (error) {
            console.log(error)
          }
        }
        break
      }
      case "": {
        indoorRef.current.showVenueObjects()
        setMapLayout("bottom-half")
        const currentBearing = indoorRef.current.getBearing()
        const bearing = getSuitablyValueBetweenBearings(
          defaultBearing,
          currentBearing
        )
        const venuesCollection = featureCollection(venues)
        const extent = indoorRef.current.getFeatureExtent(venuesCollection)
        const center = indoorRef.current.getExtentCenter(extent)
        const zoom = indoorRef.current.getExtentZoom(extent)
        indoorRef.current.flyTo(center, {
          zoom,
          bearing,
        })
        indoorRef.current.changeLevelByOrdinal(0)
        indoorRef.current.clearHighlightElements()
        indoorRef.current.clearHighlightObject()
        break
      }
      default: {
        indoorRef.current.showVenueObjects()
        indoorRef.current.changeLevelByOrdinal(0)
        const currentBearing = indoorRef.current.getBearing()
        const bearing = getSuitablyValueBetweenBearings(0, currentBearing)
        indoorRef.current.flyTo(defaultCenter, {
          zoom: DEFAULT_ZOOM,
          bearing,
        })
        indoorRef.current.clearHighlightElements()
        indoorRef.current.clearHighlightObject()
        break
      }
    }
  }, [
    indoorRef.current,
    location.pathname,
    findFeatureById,
    navigate,
    isLoading,
    userLocation,
    isKioskMode,
    relationshipGraphLoaded,
    searchParams,
    mapLoaded,
    route,
  ])

  const handleClickElement = useCallback(
    (params) => {
      const replace = false
      const { id, feature_type, category } = params.target.properties
      switch (`${feature_type}-${category}`) {
        case "unit-room":
        case "unit-unenclosedarea":
        case "unit-recreation":
        case "kiosk-kiosk":
          // STEP: find unit's occupant or kiosk's occupant
          const occupant = features.find((f) => {
            return (
              f.properties.anchor?.properties?.unit_id === id ||
              f.properties?.kiosk_id === id
            )
          })
          if (occupant) navigate(`/maps/place/${occupant.id}`, { replace })
          break
        case "amenity-atm":
        case "amenity-elevator":
        case "amenity-escalator":
        case "amenity-stairs":
        case "amenity-information":
        case "amenity-wheelchair":
        case "amenity-restroom":
        case "amenity-restroom.male":
        case "amenity-restroom.female":
        case "amenity-restroom.wheelchair":
        case "amenity-taxi":
        case "amenity-bus":
        case "amenity-parking":
        case "amenity-parking.bicycle":
        case "amenity-parking.motorcycle":
        case "amenity-smokingarea":
        case "amenity-currencyexchange":
        case "amenity-landmark":
        case "amenity-rail.muni":
        case "amenity-boardinggate.ferry":
        case "amenity-ticketing":
        case "amenity-meetingpoint":
        case "amenity-prayerroom":
        case "amenity-firstaid":
          navigate(`/maps/place/${id}`, { replace })
          break
        default:
          break
      }
    },
    /** Remove navigate because it trigger unneccessary re-render & createFeatures in Indoormap */
    [features]
    /* eslint-enable react-hooks/exhaustive-deps */
  )

  useEffect(() => {
    const indoorMap = new IndoorMap("map", {
      defaultCenter,
      defaultZoom: DEFAULT_ZOOM,
      defaultBearing,
      mapTheme,
      onMapReady: (e) => {
        setMapLoaded(true)
        setmapReady(true)
      },
      onClickElement: handleClickElement,
      mapConfig: mapConfig,
      minZoom,
      maxZoom,
      // The pixel ratio should be later moved to app config
      pixelRatio,
    })
    indoorRef.current = indoorMap

    return () => {
      indoorRef.current = null
      indoorMap.map.remove()
    }
  }, [
    pixelRatio,
    defaultCenter,
    defaultBearing,
    mapTheme,
    handleClickElement,
    setmapReady,
    mapConfig,
    minZoom,
    maxZoom,
  ])

  /** Init with features  */
  useEffect(() => {
    if (
      indoorRef.current &&
      !indoorRef.current.elementsLoaded &&
      mapTheme &&
      dataLoaded
    ) {
      indoorRef.current.mapDecorations = decorations
      indoorRef.current.features = features
      indoorRef.current.groundLabels = labels
    }
  }, [indoorRef, features, mapTheme, labels, decorations, dataLoaded])

  return (
    <Box sx={indoorMapContainer} className={mapLayout}>
      <div id="map"></div>
    </Box>
  )
}
